import styled, {css, keyframes} from 'styled-components';
import {DEVICE} from '@chekin/guestapp-ui-kit';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 12px;
  width: 100%;
  max-width: 320px;
  animation: ${slideUp} 0.5s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: backwards;

  @media (min-width: ${DEVICE.laptop}) {
    margin-bottom: 20px;
    max-width: 400px;
  }
`;

export const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${({theme}) => theme.colors.branding.fontPrimary};
  margin: 0 0 4px 0;

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 28px;
    margin: 0 0 8px 0;
  }
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: ${({theme}) => theme.colors.branding.fontSecondary};
  margin: 0;
  line-height: 1.4;

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 16px;
  }
`;

export const CardStackContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 480px;
  margin: 0 auto;
  animation: ${slideUp} 0.6s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: backwards;

  @media (min-width: 375px) {
    max-width: 340px;
    height: 520px;
  }

  @media (min-width: ${DEVICE.mobileXL}) {
    max-width: 380px;
    height: 560px;
  }

  @media (min-width: ${DEVICE.laptop}) {
    max-width: 430px;
    height: 600px;
  }
`;

type DealCardContainerProps = {
  $isDragging: boolean;
  $translateX: number;
  $rotation: number;
  $stackIndex: number;
  $isExiting: boolean;
  $exitDirection: 'left' | 'right' | null;
};

const exitLeft = keyframes`
  from {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
  to {
    transform: translateX(-150%) rotate(-30deg);
    opacity: 0;
  }
`;

const exitRight = keyframes`
  from {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
  to {
    transform: translateX(150%) rotate(30deg);
    opacity: 0;
  }
`;

export const DealCardContainer = styled.div<DealCardContainerProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  background: ${({theme}) => theme.colors.branding.boxesModals};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  cursor: ${({$isDragging}) => ($isDragging ? 'grabbing' : 'grab')};
  user-select: none;
  touch-action: pan-y;
  -webkit-touch-callout: none;
  display: flex;
  flex-direction: column;

  @media (min-width: ${DEVICE.laptop}) {
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  ${({$stackIndex}) => css`
    transform: scale(${1 - $stackIndex * 0.04}) translateY(${$stackIndex * 8}px);
    z-index: ${10 - $stackIndex};
    opacity: ${$stackIndex > 2 ? 0 : 1 - $stackIndex * 0.12};
  `}

  ${({$isDragging, $translateX, $rotation, $stackIndex}) =>
    $isDragging &&
    $stackIndex === 0 &&
    css`
      transform: translateX(${$translateX}px) rotate(${$rotation}deg);
      transition: none;
    `}

  ${({$isExiting, $exitDirection}) =>
    $isExiting &&
    css`
      animation: ${$exitDirection === 'left' ? exitLeft : exitRight} 0.3s ease-out
        forwards;
    `}

  ${({$isDragging, $isExiting}) =>
    !$isDragging &&
    !$isExiting &&
    css`
      transition: transform 0.3s ease-out, opacity 0.3s ease-out;
    `}
`;

export const CardImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 25%;
  min-height: 100px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  flex-shrink: 0;

  @media (min-width: ${DEVICE.laptop}) {
    height: 30%;
    min-height: 140px;
  }
`;

export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CardGradientOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5), transparent);
`;

type SwipeIndicatorProps = {
  $direction: 'left' | 'right';
  $opacity: number;
};

export const SwipeIndicator = styled.div<SwipeIndicatorProps>`
  position: absolute;
  top: 12px;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: ${({$opacity}) => $opacity};
  transition: opacity 0.1s ease-out;
  z-index: 10;

  @media (min-width: ${DEVICE.laptop}) {
    top: 16px;
    padding: 10px 18px;
    font-size: 16px;
  }

  ${({$direction}) =>
    $direction === 'left'
      ? css`
          right: 12px;
          background: rgba(239, 68, 68, 0.9);
          color: white;
          border: 2px solid #ef4444;
          transform: rotate(12deg);

          @media (min-width: ${DEVICE.laptop}) {
            right: 16px;
            border-width: 3px;
            transform: rotate(15deg);
          }
        `
      : css`
          left: 12px;
          background: rgba(34, 197, 94, 0.9);
          color: white;
          border: 2px solid #22c55e;
          transform: rotate(-12deg);

          @media (min-width: ${DEVICE.laptop}) {
            left: 16px;
            border-width: 3px;
            transform: rotate(-15deg);
          }
        `}
`;

export const CardContent = styled.div`
  padding: 10px 12px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  gap: 6px;

  @media (min-width: ${DEVICE.laptop}) {
    padding: 16px 20px 20px;
    gap: 10px;
  }
`;

export const CardTitle = styled.h2`
  font-size: ${({theme}) => theme.fonts.sizes.large};
  font-weight: ${({theme}) => theme.fonts.fontWeight.bold};
  color: ${({theme}) => theme.colors.branding.fontPrimary};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  line-height: 1.3;
  flex-shrink: 0;

  @media (min-width: ${DEVICE.laptop}) {
    font-size: ${({theme}) => theme.fonts.sizes.largeL};
    -webkit-line-clamp: 2;
  }
`;

export const CardHighlight = styled.p`
  font-size: ${({theme}) => theme.fonts.sizes.smallX};
  color: ${({theme}) => theme.colors.branding.fontSecondary};
  margin: 0;
  margin-bottom: auto;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  line-height: 1.4;
  min-height: 18px;
  max-height: 72px;
  padding-bottom: 8px;

  @media (min-width: ${DEVICE.laptop}) {
    font-size: ${({theme}) => theme.fonts.sizes.small};
    min-height: 20px;
    max-height: 80px;
    padding-bottom: 12px;
    -webkit-line-clamp: 4;
  }
`;

export const PriceBadgeContainer = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  z-index: 5;

  @media (min-width: ${DEVICE.laptop}) {
    bottom: 16px;
    left: 16px;
  }
`;

export const CardActions = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 24px;
  margin-top: auto;
  flex-shrink: 0;

  @media (min-width: ${DEVICE.laptop}) {
    gap: 32px;
  }
`;

export const ProgressIndicator = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  justify-content: center;

  @media (min-width: ${DEVICE.laptop}) {
    gap: 8px;
    margin-bottom: 16px;
  }
`;

type ProgressDotProps = {
  $active: boolean;
  $completed: boolean;
};

export const ProgressDot = styled.div<ProgressDotProps>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  transition: all 0.2s ease-out;

  @media (min-width: ${DEVICE.laptop}) {
    width: 8px;
    height: 8px;
  }

  ${({$active, $completed, theme}) =>
    $completed
      ? css`
          background: ${theme.colors.branding.primary};
          transform: scale(1);
        `
      : $active
      ? css`
          background: ${theme.colors.branding.primary};
          transform: scale(1.25);
        `
      : css`
          background: ${theme.colors.branding.fontSecondary};
          opacity: 0.3;
        `}
`;

const swipeAnimation = keyframes`
  0%, 15% {
    transform: translateX(0);
    opacity: 1;
  }
  35% {
    transform: translateX(-50px);
    opacity: 1;
  }
  50%, 65% {
    transform: translateX(0);
    opacity: 1;
  }
  85% {
    transform: translateX(50px);
    opacity: 1;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

export const TutorialOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  cursor: pointer;

  @media (min-width: ${DEVICE.laptop}) {
    border-radius: 20px;
  }
`;

export const TutorialContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 0 20px;

  @media (min-width: ${DEVICE.laptop}) {
    gap: 24px;
  }
`;

export const HandIconWrapper = styled.div`
  animation: ${swipeAnimation} 2.5s ease-in-out infinite;

  svg {
    width: 48px;
    height: 48px;
  }

  @media (min-width: ${DEVICE.laptop}) {
    svg {
      width: 64px;
      height: 64px;
    }
  }
`;

export const TutorialText = styled.p`
  color: white;
  font-size: 14px;
  text-align: center;
  max-width: 240px;
  line-height: 1.5;
  margin: 0;

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 16px;
    max-width: 280px;
  }
`;

export const TutorialHint = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 14px;
  }
`;

export const SwipeLabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 160px;

  @media (min-width: ${DEVICE.laptop}) {
    width: 200px;
  }
`;

export const SwipeLabel = styled.span<{$direction: 'left' | 'right'}>`
  font-size: 12px;
  font-weight: 600;
  color: ${({$direction}) => ($direction === 'left' ? '#ef4444' : '#22c55e')};

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 14px;
  }
`;

export const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${({theme}) => theme.colors.branding.backgroundMain};
  border-radius: 10px;
  border: 1px solid ${({theme}) => theme.colors.branding.boxesModals};

  @media (min-width: ${DEVICE.laptop}) {
    padding: 12px 16px;
  }
`;

export const QuantityLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const QuantityTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.branding.fontPrimary};

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 15px;
  }
`;

export const QuantityPrice = styled.span`
  font-size: 12px;
  color: ${({theme}) => theme.colors.branding.fontSecondary};

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 13px;
  }
`;

export const TimeBasedSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: ${({theme}) => theme.colors.branding.backgroundMain};
  border-radius: 10px;
  border: 1px solid ${({theme}) => theme.colors.branding.boxesModals};
  flex-shrink: 0;

  @media (min-width: ${DEVICE.laptop}) {
    padding: 12px 16px;
    gap: 10px;
  }
`;

export const TimeBasedTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.branding.fontPrimary};

  svg {
    color: ${({theme}) => theme.colors.branding.icons};
    flex-shrink: 0;
  }
`;

export const TimeDropdown = styled.div`
  position: relative;
  z-index: 30;
`;

export const TimeDropdownButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 48px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.colors.branding.primary};
  background: ${({theme}) => theme.colors.branding.boxesModals};
  color: ${({theme}) => theme.colors.branding.fontPrimary};
  cursor: pointer;
  text-align: left;

  svg {
    color: ${({theme}) => theme.colors.branding.icons};
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({theme}) => theme.colors.branding.primary};
  }
`;

export const TimeDropdownButtonContent = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const TimeDropdownValue = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
`;

export const TimeDropdownPrice = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.branding.fontSecondary};
`;

export const TimeDropdownMenu = styled.div`
  position: relative;
  z-index: 20;
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
  max-height: 178px;
  margin-top: 6px;
  overflow-y: auto;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.colors.branding.boxesModals};
  background: ${({theme}) => theme.colors.branding.boxesModals};
  box-shadow: 0 10px 24px rgba(21, 41, 70, 0.14);

  @media (min-width: ${DEVICE.laptop}) {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    max-height: 220px;
    margin-top: 0;
  }
`;

export const TimeOption = styled.button<{$selected: boolean}>`
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 34px 9px 10px;
  border: 0;
  border-radius: 7px;
  background: ${({$selected, theme}) =>
    $selected
      ? theme.colors.navigationMenu.selectedItemBackground
      : theme.colors.branding.backgroundMain};
  color: ${({theme}) => theme.colors.branding.fontPrimary};
  cursor: pointer;
  text-align: left;
  transition: background 0.2s ease;

  &:hover {
    background: ${({theme}) => theme.colors.navigationMenu.selectedItemBackground};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({theme}) => theme.colors.branding.primary};
  }
`;

export const TimeOptionLabel = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
`;

export const TimeOptionPrice = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: ${({theme}) => theme.colors.branding.fontSecondary};
`;

export const TimeOptionCheck = styled.span`
  position: absolute;
  top: 50%;
  right: 10px;
  display: flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: ${({theme}) => theme.colors.branding.primary};
  color: ${({theme}) => theme.colors.branding.backgroundMain};
  transform: translateY(-50%);
`;

export const TotalPriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 0;
  margin-top: 12px;

  @media (min-width: ${DEVICE.laptop}) {
    padding: 8px 0;
    margin-top: 16px;
  }
`;

export const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({theme}) => theme.colors.branding.fontSecondary};
`;

export const TotalAmount = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${({theme}) => theme.colors.branding.fontPrimary};

  @media (min-width: ${DEVICE.laptop}) {
    font-size: 20px;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const SkeletonCard = styled.div<{$stackIndex: number}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
  background: ${({theme}) => theme.colors.branding.boxesModals};
  display: flex;
  flex-direction: column;

  @media (min-width: ${DEVICE.laptop}) {
    border-radius: 20px;
  }

  ${({$stackIndex}) => css`
    transform: scale(${1 - $stackIndex * 0.04}) translateY(${$stackIndex * 8}px);
    z-index: ${10 - $stackIndex};
    opacity: ${$stackIndex > 2 ? 0 : 1 - $stackIndex * 0.12};
  `}
`;

export const SkeletonImage = styled.div`
  width: 100%;
  height: 25%;
  min-height: 100px;
  background: linear-gradient(
    90deg,
    ${({theme}) => theme.colors.branding.backgroundMain} 25%,
    ${({theme}) => theme.colors.branding.boxesModals} 50%,
    ${({theme}) => theme.colors.branding.backgroundMain} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export const SkeletonContent = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: ${DEVICE.laptop}) {
    padding: 20px;
    gap: 16px;
  }
`;

export const SkeletonLine = styled.div<{$width?: string; $height?: string}>`
  width: ${({$width}) => $width || '100%'};
  height: ${({$height}) => $height || '16px'};
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    ${({theme}) => theme.colors.branding.backgroundMain} 25%,
    ${({theme}) => theme.colors.branding.boxesModals} 50%,
    ${({theme}) => theme.colors.branding.backgroundMain} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export const SkeletonButtonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: auto;
`;

export const SkeletonButton = styled.div`
  flex: 1;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    ${({theme}) => theme.colors.branding.backgroundMain} 25%,
    ${({theme}) => theme.colors.branding.boxesModals} 50%,
    ${({theme}) => theme.colors.branding.backgroundMain} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;

  @media (min-width: ${DEVICE.laptop}) {
    height: 42px;
  }
`;
